import React, { useState, useMemo } from 'react';
import { Calendar, MessageSquare, Send, X, MapPin, CheckCircle, Building2, ChevronLeft } from 'lucide-react';
import { proposalTokens } from './tokens';
import './proposal.css';

interface Hotel {
  id: string;
  name: string;
  stars: number;
  image: string;
  images?: string[];
  description?: string;
  roomCount?: number;
  meetingSpaces?: string[];
  priceRange?: string;
  highlights?: string[];
  recommended?: boolean;
}

interface Destination {
  id: string;
  city: string;
  country: string;
  heroImage?: string;
  tagline?: string;
}

interface ShortlistItem {
  destinationId: string;
  hotelId: string;
  destinationCity: string;
  destinationCountry: string;
  hotelName: string;
  hotelImage: string;
  estimatedPerPerson?: number;
}

interface SignOffProps {
  shortlistedHotels: ShortlistItem[];
  onRemoveFromShortlist: (hotelId: string) => void;
  /** False when the team has hidden the budget; the per-person estimate is a budget figure. */
  showEstimates?: boolean;
  onSendFeedback: (name: string, comments: string) => Promise<void>;
  onBookReviewCall: () => void;
  onNavigate?: (id: string) => void;
}

export function SignOff({ 
  shortlistedHotels,
  onRemoveFromShortlist,
  onSendFeedback,
  onBookReviewCall,
  onNavigate,
  showEstimates = true,
}: SignOffProps) {
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackComments, setFeedbackComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSendFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccess(false);
    try {
      await onSendFeedback(feedbackName, feedbackComments);
      setFeedbackName('');
      setFeedbackComments('');
      setShowSuccess(true);
      // Keep form open - don't close it
    } catch (error) {
      console.error('Failed to send feedback:', error);
      // Keep form open on error
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-cream text-teal p-8 md:p-12 hide-scrollbar">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-12">
          <h1 className="text-4xl font-serif font-bold text-teal mb-4">
            Ready to Move Forward?
          </h1>
          <p className="text-teal/60 max-w-3xl">
            We are excited to help bring your program's vision and team together!
          </p>
        </div>

        {/* Action Buttons Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Share Feedback Button */}
          <button
            onClick={() => setShowFeedbackForm(!showFeedbackForm)}
            className={`
              flex flex-col items-center justify-center p-8 rounded-2xl border-2 transition-all group
              ${showFeedbackForm 
                ? 'bg-teal text-white border-teal shadow-lg' 
                : 'bg-white border-black/10 hover:border-teal/30 hover:bg-teal/5'
              }
            `}
          >
            <MessageSquare className={`w-10 h-10 mb-4 transition-transform ${showFeedbackForm ? 'text-white' : 'text-teal group-hover:scale-110'}`} />
            <span className={`font-bold text-lg mb-2 ${showFeedbackForm ? 'text-white' : 'text-teal'}`}>
              {showFeedbackForm ? 'Hide Feedback' : 'Share Feedback'}
            </span>
            <span className={`text-sm text-center ${showFeedbackForm ? 'text-white/80' : 'text-teal/60'}`}>
              {showFeedbackForm ? 'Click to hide the form' : 'Share your thoughts and questions'}
            </span>
          </button>

          {/* Book Review Call Button */}
          <button
            onClick={onBookReviewCall}
            className="flex flex-col items-center justify-center p-8 bg-teal text-white rounded-2xl hover:bg-teal/90 transition-all shadow-lg hover:shadow-xl group"
          >
            <Calendar className="w-10 h-10 mb-4 group-hover:scale-110 transition-transform" />
            <span className="font-bold text-lg mb-2">Book a Review Call</span>
            <span className="text-sm text-white/80 text-center">Schedule 30 min with a member of our team</span>
          </button>
        </div>

        {/* Feedback Form */}
        {showFeedbackForm && (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-black/10 shadow-lg animate-fade-in mb-8">
            <h3 className="text-2xl font-serif font-bold text-teal mb-6 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-teal" />
              Share Feedback
            </h3>
            
            {/* Success Message */}
            {showSuccess && (
              <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 flex items-start gap-3 animate-fade-in">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Thank you for your feedback!</p>
                  <p className="text-sm text-green-700 mt-1">Your feedback has been submitted successfully. We'll review it and get back to you soon.</p>
                </div>
                <button
                  onClick={() => setShowSuccess(false)}
                  className="text-green-600 hover:text-green-800 transition-colors"
                  aria-label="Dismiss success message"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <form onSubmit={handleSendFeedback} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-teal/60 mb-2">Your Name</label>
                <input 
                  type="text" 
                  value={feedbackName}
                  onChange={(e) => {
                    setFeedbackName(e.target.value);
                    setShowSuccess(false); // Clear success message when user starts typing
                  }}
                  className="w-full bg-cream border border-black/20 rounded-lg px-4 py-3 text-teal focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all"
                  placeholder="Jane Doe"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-teal/60 mb-2">Comments or Questions</label>
                <textarea 
                  value={feedbackComments}
                  onChange={(e) => {
                    setFeedbackComments(e.target.value);
                    setShowSuccess(false); // Clear success message when user starts typing
                  }}
                  className="w-full bg-cream border border-black/20 rounded-lg px-4 py-3 text-teal focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all h-32 resize-none"
                  placeholder="e.g. We love Lisbon but are worried about the hill walking for some execs..."
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex justify-end">
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 text-sm font-bold rounded-md hover:opacity-90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: proposalTokens.colors.gold, color: '#000000' }}
                >
                  <Send className="w-4 h-4" />
                  {isSubmitting ? 'Sending...' : 'Send Feedback'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Shortlist Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-serif font-bold text-teal mb-2">Your Shortlist</h2>
          <p className="text-teal/60 mb-6">Your selected destinations and accommodations.</p>
          {shortlistedHotels.length > 0 ? (
            <div className="space-y-4">
              {shortlistedHotels.map((item) => (
                <div 
                  key={item.hotelId}
                  className="bg-white rounded-2xl p-4 border border-black/10 shadow-sm hover:shadow-md transition-all group flex flex-col sm:flex-row gap-4 items-center"
                >
                  {/* Thumbnail Image */}
                  <div className="w-full sm:w-32 h-32 sm:h-24 shrink-0 rounded-xl overflow-hidden relative bg-gray-100 flex items-center justify-center">
                    {item.hotelImage ? (
                      <img
                        src={item.hotelImage}
                        alt={item.hotelName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Building2 className="w-8 h-8 text-gray-400" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 text-center sm:text-left">
                    <h3 className="font-serif font-bold text-xl text-teal mb-1 truncate">
                      {item.hotelName}
                    </h3>
                    <div className="flex items-center justify-center sm:justify-start text-teal/60 text-sm gap-4">
                      <span className="flex items-center">
                         <MapPin className="w-3 h-3 mr-1" />
                         {item.destinationCity}, {item.destinationCountry}
                      </span>
                    </div>
                  </div>

                  {/* Price & Action */}
                  <div className="flex flex-row sm:flex-col items-center sm:items-end gap-4 sm:gap-1 w-full sm:w-auto justify-between sm:justify-center border-t sm:border-t-0 border-black/5 pt-4 sm:pt-0">
                    {showEstimates && item.estimatedPerPerson && (
                      <div className="text-left sm:text-right">
                        <span className="text-[10px] text-teal/40 uppercase font-bold block">Est. Per Person</span>
                        <span className="font-bold text-lg text-teal">
                          ${item.estimatedPerPerson.toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    <button
                      onClick={() => onRemoveFromShortlist(item.hotelId)}
                      className="w-8 h-8 rounded-full bg-cream border border-black/10 hover:bg-red-50 hover:border-red-200 text-teal/40 hover:text-red-500 flex items-center justify-center transition-colors"
                      title="Remove from shortlist"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 border border-black/10 shadow-sm text-center">
              <p className="text-teal/60 italic">
                You haven't shortlisted any specific destinations yet. No problem, we can discuss all options!
              </p>
            </div>
          )}
        </div>

        {/* Back to Proposal Button */}
        {onNavigate && (
          <div className="mt-12 flex justify-center">
            <button
              onClick={() => onNavigate('overview')}
              className="px-8 py-4 text-base font-bold rounded-md hover:opacity-90 transition-colors flex items-center gap-2 border border-black/20 hover:bg-black/5"
              style={{ color: proposalTokens.colors.teal }}
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Proposal
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

